import { Injectable } from '@angular/core';
import { extractFromLocalizedString } from '@examdojo/core/i18n';
import { SvgIcon } from '@examdojo/core/icon';
import { ErrorHandlerService } from '@examdojo/error-handling';
import { rethrowError, shareOneReplay } from '@examdojo/rxjs';
import { ViewRowModel } from '@examdojo/supabase';
import { map, Observable, tap } from 'rxjs';
import { CourseHttpModel, CourseLevel, CourseStoreModel, getCourseCompositeId, Program } from '../model';
import { SyllabusHttpService } from './syllabus-http.service';
import { SyllabusAndCourseLevel, SyllabusHttpModel, SyllabusStoreModel } from './syllabus.model';
import { SyllabusQuery } from './syllabus.query';
import { SyllabusStore } from './syllabus.store';

@Injectable({
  providedIn: 'root',
})
export class SyllabusService {
  constructor(
    private readonly store: SyllabusStore,
    private readonly query: SyllabusQuery,
    private readonly syllabusHttpService: SyllabusHttpService,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {}

  private cachedSyllabuses$?: Observable<SyllabusStoreModel[]>;

  fetchSyllabuses(): Observable<SyllabusStoreModel[]> {
    if (this.cachedSyllabuses$) {
      return this.cachedSyllabuses$;
    }

    const request$ = this.syllabusHttpService.fetchAllWithPrograms().pipe(
      map((data) => data.map((item) => this.mapSyllabusHttpModelToStoreModel(item))),
      tap((storeModels) => {
        this.store.set(storeModels);
      }),
      this.errorHandlerService.catchError('[CategoryService]: Fetching syllabuses failed', rethrowError(), {
        toast: 'An error occurred while fetching the syllabuses',
      }),
      shareOneReplay(),
    );

    this.cachedSyllabuses$ = request$;

    return request$;
  }

  fetchActiveSyllabuses(filters?: { programId?: Program }): Observable<SyllabusStoreModel[]> {
    return this.syllabusHttpService.fetchAllActiveWithPrograms(filters).pipe(
      map((data) => data.map((item) => this.mapSyllabusHttpModelToStoreModel(item))),
      tap((storeModels) => {
        this.store.set(storeModels);
      }),
      this.errorHandlerService.catchError('[CategoryService]: Fetching active syllabuses failed', rethrowError(), {
        toast: 'An error occurred while fetching the active syllabuses',
      }),
    );
  }

  setActive(id: SyllabusStoreModel['id'] | null, course_level: CourseLevel | null = null): void {
    this.store.setActive(id);
    this.setCourseLevel(course_level);
  }

  setCourseLevel(course_level: CourseLevel | null): void {
    this.store.updateProps(() => ({ course_level }));
  }

  /**
   * Set active syllabus by slug
   */
  setActiveBySlug(slug: string, course_level: CourseLevel | null = null): boolean {
    const syllabus = this.query.getBySlug(slug);
    if (syllabus) {
      this.setActive(syllabus.id, course_level);
      return true;
    }
    return false;
  }

  fetchCourses(program?: Program): Observable<CourseStoreModel[]> {
    return this.syllabusHttpService.fetchCourses(program).pipe(
      this.errorHandlerService.setHttpErrorMetadata({ entity: 'examdojo.entity.course' }),
      map((courses) => courses.map(this.mapCourseHttpModelToStoreModel)),
      tap((courses) => this.store.updateProps(() => ({ courses }))),
    );
  }

  fetchSubscribedCoursesForProgram(program: Program): Observable<SyllabusAndCourseLevel[]> {
    return this.syllabusHttpService
      .fetchSubscribedCoursesForProgram(program)
      .pipe(this.errorHandlerService.setHttpErrorMetadata({ entity: 'examdojo.entity.course' }));
  }

  resetCourses() {
    this.store.updateProps(() => ({ courses: undefined }));
  }

  clearCache() {
    this.cachedSyllabuses$ = undefined;
  }

  private mapSyllabusHttpModelToStoreModel(
    httpModel: SyllabusHttpModel & ViewRowModel<'syllabuses_with_programs', 'categories'>,
  ): SyllabusStoreModel {
    const program = httpModel.program;

    return {
      ...httpModel,
      name: extractFromLocalizedString(httpModel.name),
      short_name: extractFromLocalizedString(httpModel.short_name),
      description: extractFromLocalizedString(httpModel.description),
      program: {
        code: program,
        short_name: httpModel.program_short_name ?? program,
        full_name: httpModel.program_full_name ?? program,
        icon: httpModel.program_icon as SvgIcon | null,
      },
    };
  }

  private mapCourseHttpModelToStoreModel(httpModel: CourseHttpModel): CourseStoreModel {
    const fallbackLabel = httpModel.name
      ? `${httpModel.name}${httpModel.course_level ? ` ${httpModel.course_level}` : ''}`
      : httpModel.slug.toUpperCase();

    const icon = (httpModel.course_icon as SvgIcon | null) ?? (httpModel.program_icon as SvgIcon | null) ?? undefined;

    return {
      ...httpModel,
      syllabus_id: httpModel.id,
      longLabel: httpModel.course_full_name ?? fallbackLabel,
      shortLabel: httpModel.course_short_name ?? fallbackLabel,
      icon,
      compositeId: getCourseCompositeId({
        syllabusId: httpModel.id,
        courseLevel: httpModel.course_level,
      }),
    };
  }
}
